import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { forgetLocationPolicy } from '../lib/apiClient';
import { loadPhotos } from '../lib/photos';
import PhotoModeration from './PhotoModeration';
import { useSettings } from '../pages/settings/SettingsContext';
import TodoWorkloadRule from './TodoWorkloadRule';
import './AppRules.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Decisions NCMB has made about how Jarvis behaves.
 *
 * Every other tab in Settings is a list of things — users, departments,
 * categories, priorities, statuses, custom fields, SLA rules. A rule is not a
 * row in a list, which is why there was nowhere to put one and why the first
 * one Gordon asked for went unbuilt: approvers became mandatory, he asked for
 * that to be optional, and there was no screen it could live on.
 *
 * Each rule says what it does in both positions, not just the one it is in.
 * "Require an approver ☑" tells you nothing about what unticking it would mean,
 * and the thing people assume — that the ticket skips approval altogether — is
 * exactly what does NOT happen.
 *
 * ONE COMPONENT, SEVERAL PAGES (26 October). This was the whole of Settings ->
 * Rules, nine subjects on one long page. The Settings reorganisation put each
 * subject where somebody looks for it - the ticket rules under Tickets &
 * workflow, messaging and recording under Chat, calls & meetings, photos
 * under People & access - and every one of those pages draws this with
 * `section` naming its part. The text and the behaviour are unchanged; each
 * block carries the id the Settings search jumps to (s-<anchor>).
 *
 *   section   'approver' | 'private-notes' | 'location' | 'digest' |
 *             'messaging' | 'groups' | 'photos' | 'calls' | 'review' |
 *             'recording', or a list of them; every one when absent
 *   onChanged called after every save, so the Settings shell's copy (the
 *             search's values, the Overview) follows at once
 */
const SECTIONS = ['approver', 'private-notes', 'location', 'digest', 'messaging', 'groups',
  'photos', 'calls', 'review', 'recording', 'todo-workload'];

const AppRules = ({ canEdit = true, section = null, onChanged = null }) => {
  const shown = section ? [].concat(section) : SECTIONS;
  const show = (k) => shown.includes(k);
  const { features } = useSettings();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/app-settings`);
      setRules(res.data || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read the current settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /*
   * One saver for every rule, keyed by its own name.
   *
   * There was one function per rule and exactly one rule, so adding the
   * second would have meant copying five lines and changing two strings -
   * which is how the two drift and how one of them ends up without the
   * reload that makes the screen agree with the server.
   */
  /*
   * A number rather than a switch, saved when the box loses focus rather than
   * on every keystroke - typing "14" would otherwise save 1 on the way to 14,
   * and 1am is a real hour the digest would have gone out at.
   */
  const setNumber = async (key, path, value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    await setRule(key, path, Math.round(n));
  };

  const setRule = async (key, path, value) => {
    setSaving(key);
    setError(null);
    try {
      await axios.put(`${API_BASE}/app-settings/${path}`, { value });
      // This tab stops (or starts) asking at once, not a minute from now.
      if (key === 'ask_location') forgetLocationPolicy();
      // Every circle on this page follows the new photo rule at once.
      if (key === 'profile_photos') loadPhotos({ force: true });
      await load();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that');
    } finally {
      setSaving(null);
    }
  };

  if (error && !rules) return <p className="ar-error">{error}</p>;
  if (!rules) return <p className="ar-quiet">Loading…</p>;

  const required = rules.require_approver !== false;
  // Defaults to OFF when absent, matching lib/settings.js - a database
  // without migration 033 is not enforcing this, and the screen must not
  // claim it is.
  const locating = rules.require_location === true;
  // On when absent, matching lib/settings.js askLocation: asking is what
  // Jarvis has done since locations were first recorded.
  const asking = rules.ask_location !== false;
  // Both default on the server too, so a database without migration 024 shows
  // the behaviour it actually has rather than a blank box.
  const hour = Number.isFinite(Number(rules.approval_digest_hour))
    ? Number(rules.approval_digest_hour) : 8;
  const after = Number.isFinite(Number(rules.approval_digest_after))
    ? Number(rules.approval_digest_after) : 0;
  // All three default to off, which is today's behaviour: you can message the
  // people you share a ticket with, and an administrator can reach anyone.
  const s2s = rules.chat_staff_to_staff === true;
  const c2c = rules.chat_contractor_to_contractor === true;
  const openChat = rules.chat_open === true;
  // Off by default and off when absent, matching lib/settings.js: a call
  // needs a ticket until somebody decides otherwise here.
  const freeCalls = rules.calls_without_ticket === true;
  // Off when absent, matching lib/settings.js (086).
  const privateNotes = rules.ticket_private_notes === true;
  // Off by default and off when absent, matching lib/settings.js: nobody
  // reads anybody else's conversations until somebody decides they may.
  const adminReview = rules.chat_admin_review === true;
  // Staff when absent or unrecognised, matching the server's default.
  const groupsWho = ['admins', 'staff', 'everyone'].includes(rules.chat_groups_who)
    ? rules.chat_groups_who : 'staff';
  // 079: off when absent, matching lib/settings.js; the list empty when absent.
  const contractorGroups = rules.chat_groups_contractors === true;
  const contractorRules = Array.isArray(rules.chat_group_rules_contractors)
    ? rules.chat_group_rules_contractors : [];
  // Recording (065). WHETHER anybody may record is the feature switch now -
  // "Calls can be recorded" was folded into it (migration 088) - and it is
  // drawn above these, on the same page. The two choices below apply while
  // somebody may record: the switch is not Off, or it is Off with people
  // under "Also allow". Before 088 the old rule can still say off, and that
  // is said rather than hidden. Announce when absent or unrecognised, and
  // ninety days, both the server's defaults.
  const recFeature = (features?.features || []).find((f) => f.key === 'recording');
  const legacyOff = rules.recording_legacy_off === true;
  const recording = !legacyOff
    && !(recFeature && recFeature.audience === 'off' && !(recFeature.allow || []).length);
  const consent = rules.recording_consent === 'ask' ? 'ask' : 'announce';
  // Profile photos (080): allowed when absent or unrecognised, as the server.
  const photosMode = ['allowed', 'staff', 'off'].includes(rules.profile_photos)
    ? rules.profile_photos : 'allowed';
  // 23 October: what a recording is transcribed in when nobody chooses.
  // Malti + English when absent or unrecognised, as the server.
  const recLanguage = ['mixed', 'auto', 'en-GB', 'mt-MT', 'it-IT', 'de-DE', 'fr-FR'].includes(rules.recording_language)
    ? rules.recording_language : 'mixed';
  const retention = Number.isFinite(Number(rules.recording_retention_days))
    ? Math.max(0, Math.round(Number(rules.recording_retention_days))) : 90;

  return (
    <div className="ar">
      {show('approver') && (
      <div className="ar-rule" id="s-approver">
        <label className="ar-check">
          <input
            type="checkbox"
            checked={required}
            disabled={!canEdit || saving === 'require_approver'}
            onChange={() => setRule('require_approver', 'require-approver', !required)}
          />
          <span className="ar-label">Every ticket must name an approver</span>
        </label>

        {/* What each position actually means. Without this, unticking reads as
            "tickets no longer need approving", which is not what happens and
            is the hole the mandatory rule was introduced to close. */}
        <p className="ar-consequence">
          {required
            ? 'On: the Create Ticket form will not submit without an approver, '
              + 'and neither will the API.'
            : 'Off: a ticket can be raised without naming one. It still enters '
              + 'the approval queue — it waits for whichever approver picks it '
              + 'up instead of a named person. Nothing skips approval.'}
        </p>
      </div>
      )}

      {/*
        * PRIVATE NOTES ON A TICKET (migration 086). Gordon: comments stay
        * open to everybody on a ticket by default; this is the option. The
        * server decides who reads a note (lib/private-notes.js) - this only
        * says, in both positions, what that means. Off by default and off on
        * a database without the migration, matching lib/settings.js.
        */}
      {show('private-notes') && (
      <div className="ar-rule" id="s-private-notes">
        <label className="ar-check">
          <input
            type="checkbox"
            checked={privateNotes}
            disabled={!canEdit || saving === 'ticket_private_notes'}
            onChange={() => setRule('ticket_private_notes', 'ticket-private-notes', !privateNotes)}
          />
          <span className="ar-label">Allow private notes on tickets</span>
        </label>
        <p className="ar-consequence">
          {privateNotes
            ? 'On: IT staff, approvers and administrators can tick “Private note” '
              + 'under a ticket’s reply box. The note is marked “Private – only IT '
              + 'and approvers see this”, and the person who raised the ticket and '
              + 'the contractor never see it — not on the ticket, in chat, in '
              + 'search, in the bell or in an email.'
            : 'Off: everything written on a ticket is seen by everybody on it. '
              + 'Private notes written while this was on stay private; turning it '
              + 'off only stops new ones.'}
        </p>
      </div>
      )}

      {/* WHO SEES THE TEAM WORKLOAD (089) - its own component, TodoWorkloadRule.js;
          drawn by Settings > Apps & content > To do & calendars. */}
      {show('todo-workload') && (
      <div id="s-todo-workload">
        <TodoWorkloadRule rules={rules} canEdit={canEdit} saving={saving} setRule={setRule} />
      </div>
      )}

      {/*
        * THE WAY OUT, and it was missing.
        *
        * Gordon chose to BLOCK a change when the browser will not report a
        * location, over my advice. I built the rule, the 428, and a panel
        * that tells people "an administrator can switch this requirement off
        * in Settings" - and never built the switch. The error message made a
        * promise the application could not keep, which is worse than no
        * message at all: somebody reading it goes looking for a control that
        * does not exist.
        *
        * It matters most on the morning it matters: a browser that will not
        * cooperate, somebody on site who cannot update their own ticket, and
        * a fix that should take thirty seconds rather than a deploy.
        */}
      {/*
        * THE SWITCH ABOVE IT. Gordon, 19 October: "The location settings
        * for Jarvis chat & similar features - can we have this as an option
        * in the settings so that it can be switched off." The rule below only
        * decided whether a change is REFUSED without a location; the browser
        * was asked either way, on every message typed on a ticket's chat.
        * Off here, nobody is asked and the rule below has nothing to enforce.
        */}
      {show('location') && (
      <>
      <div className="ar-rule" id="s-ask-location">
        <label className="ar-check">
          <input
            type="checkbox"
            checked={asking}
            disabled={!canEdit || saving === 'ask_location'}
            onChange={() => setRule('ask_location', 'ask-location', !asking)}
          />
          <span className="ar-label">Ask for people&rsquo;s location</span>
        </label>

        <p className="ar-consequence">
          {asking
            ? 'On: when somebody comments on a ticket (which includes writing in '
              + 'a ticket’s chat), changes its status, edits it, hands it back or '
              + 'logs hours, their browser is asked where they are and the answer '
              + 'is kept with the change. The browser asks their permission first.'
            : 'Off: nobody’s browser is asked for a location, nothing new is '
              + 'stored, and nobody sees the permission prompt. Locations already '
              + 'recorded are kept. Device check-ins have their own setting under '
              + 'Assets & devices → Device agent.'}
        </p>
      </div>

      <div className="ar-rule" id="s-require-location" data-off={!asking || undefined}>
        <label className="ar-check">
          <input
            type="checkbox"
            checked={locating && asking}
            disabled={!canEdit || !asking || saving === 'require_location'}
            onChange={() => setRule('require_location', 'require-location', !locating)}
          />
          <span className="ar-label">
            A change to a ticket must record where it was made
          </span>
        </label>

        <p className="ar-consequence">
          {!asking
            ? 'Nothing to enforce while Jarvis is not asking for locations.'
            : locating
            ? 'On: comments, status changes, edits, hand-backs and logged hours '
              + 'are refused unless the browser reports a location. Somebody '
              + 'whose browser will not — or who has blocked the permission — '
              + 'cannot change a ticket until they allow it or you turn this off.'
            : 'Off: those changes go through and are recorded as having no '
              + 'location, with the reason why. Locations already recorded are '
              + 'kept — turning this off hides nothing and deletes nothing.'}
        </p>
      </div>
      </>
      )}

      {/*
        * THE APPROVAL DIGEST, which already existed and which nothing on any
        * screen mentioned.
        *
        * Gordon asked for "one digest instead of many emails - e.g. every
        * morning at 8, or once five are waiting", and the first half had been
        * running since it was written: one email per approver covering
        * everything of theirs still waiting, stamped so the same ticket is
        * never in two of them, with high and critical tickets emailed the
        * moment they are raised rather than held.
        *
        * What was missing is that the hour lived in an environment variable.
        * An administrator could not see that the digest existed, let alone
        * move it - and a rule nobody can see is a rule nobody trusts. This is
        * not a list of things to maintain, so it sits with the other two
        * decisions for the firm rather than in a tab of its own.
        */}
      {show('digest') && (
      <div className="ar-rule" id="s-digest">
        <p className="ar-label">Approvers get one email, not one per ticket</p>
        <p className="ar-consequence">
          Everything still waiting for an approver goes out as a single message
          to each of them. A ticket is only ever in one digest. High and
          critical tickets are emailed the moment they are raised instead of
          waiting.
        </p>

        <div className="ar-numbers">
          <label className="ar-number">
            <span>Goes out at</span>
            <input
              type="number" min="0" max="23" step="1"
              disabled={!canEdit || saving === 'approval_digest_hour'}
              defaultValue={hour}
              key={`hour-${hour}`}
              onBlur={(e) => Number(e.target.value) !== hour
                && setNumber('approval_digest_hour', 'approval-digest-hour', e.target.value)}
            />
            <span>:00, your time</span>
          </label>

          <label className="ar-number">
            <span>Or early, once</span>
            <input
              type="number" min="0" step="1"
              disabled={!canEdit || saving === 'approval_digest_after'}
              defaultValue={after}
              key={`after-${after}`}
              onBlur={(e) => Number(e.target.value) !== after
                && setNumber('approval_digest_after', 'approval-digest-after', e.target.value)}
            />
            <span>are waiting</span>
          </label>
        </div>

        <p className="ar-consequence">
          {after > 0
            ? `On: a queue that reaches ${after} brings the digest forward `
              + 'rather than leaving it until tomorrow morning.'
            : 'Zero leaves that off \u2014 the digest goes out once a day, at the '
              + 'hour above, however many are waiting.'}
        </p>
      </div>
      )}

      {/*
        * WHO MAY TALK TO WHOM.
        *
        * Gordon: "I also want options for contractors to contact other
        * contractors and for staff to contact other staff and also for
        * everything to be open in terms of chat and calls between all users."
        *
        * All three are off until somebody turns them on, because off is the
        * rule he set on 12 October and the reason for it is a good one for a
        * CSP firm: the alternative hands every external IT contractor a
        * browsable directory of all forty staff. So each of these says what
        * it actually opens up rather than being a preference with a tick.
        *
        * Three switches rather than one slider, because "staff can talk to
        * each other" is an internal decision and "contractors can talk to
        * each other" crosses the firm's boundary. A single level from tight
        * to open would make them one choice.
        */}
      {show('messaging') && (
      <div className="ar-rule" id="s-messaging">
        <p className="ar-label">Who can start a conversation</p>
        <p className="ar-consequence">
          Everybody can already message the people they share a ticket with,
          and you can reach anyone. These widen that.
        </p>

        <label className="ar-check" id="s-staff-to-staff">
          <input
            type="checkbox"
            checked={s2s}
            disabled={!canEdit || openChat || saving === 'chat_staff_to_staff'}
            onChange={() => setRule('chat_staff_to_staff', 'chat-staff-to-staff', !s2s)}
          />
          <span className="ar-label">Staff can message any other staff</span>
        </label>
        <p className="ar-consequence">
          {s2s
            ? 'On: anyone with an NCMB account can find and message any colleague, '
              + 'ticket or no ticket. Contractors gain nothing from this.'
            : 'Off: a member of staff sees only the colleagues they share work with.'}
        </p>

        <label className="ar-check" id="s-contractor-to-contractor">
          <input
            type="checkbox"
            checked={c2c}
            disabled={!canEdit || openChat || saving === 'chat_contractor_to_contractor'}
            onChange={() => setRule('chat_contractor_to_contractor',
              'chat-contractor-to-contractor', !c2c)}
          />
          <span className="ar-label">Contractors can message each other</span>
        </label>
        <p className="ar-consequence">
          {c2c
            ? 'On: your contractors can find each other. They still cannot see '
              + 'staff they share no ticket with.'
            : 'Off: a contractor sees only the people on their own jobs.'}
        </p>

        <label className="ar-check" id="s-chat-open">
          <input
            type="checkbox"
            checked={openChat}
            disabled={!canEdit || saving === 'chat_open'}
            onChange={() => setRule('chat_open', 'chat-open', !openChat)}
          />
          <span className="ar-label">Everyone can message everyone</span>
        </label>
        <p className="ar-consequence">
          {openChat
            ? 'On: every person in Jarvis can find and message every other one, '
              + 'including contractors reaching staff they share no work with. '
              + 'This is wider than the two above together, which is why they '
              + 'are greyed out — it already covers them.'
            : 'Off: the two switches above decide it.'}
        </p>
        {openChat && (
          <p className="ar-note">
            Your contractors can see a list of everybody at NCMB while this is on.
          </p>
        )}
      </div>
      )}

      {show('groups') && (
      <div className="ar-rule" id="s-groups">

        {/*
          * WHO MAY START A GROUP.
          *
          * Three positions on one line, so a choice of one rather than three
          * switches: they are a ladder - each includes the one before - and
          * three boxes would let somebody tick "everyone" and not "staff".
          * Staff by default, because a group is somewhere several people talk
          * at once and the firm should decide whether contractors convene
          * them. Who can be PUT in one is not a fourth setting: it is the
          * switches above, because a member can add only people they could
          * already message.
          */}
        <fieldset className="ar-choice ar-choice-first" id="s-groups-who">
          <legend className="ar-label">Who can create a group</legend>
          {[
            ['admins', 'Administrators only'],
            ['staff', 'Staff'],
            ['everyone', 'Everyone, contractors included']
          ].map(([value, label]) => (
            <label className="ar-check" key={value}>
              <input
                type="radio"
                name="chat-groups-who"
                value={value}
                checked={groupsWho === value}
                disabled={!canEdit || saving === 'chat_groups_who'}
                onChange={() => setRule('chat_groups_who', 'chat-groups-who', value)}
              />
              <span className="ar-label">{label}</span>
            </label>
          ))}
        </fieldset>
        <p className="ar-consequence">
          {groupsWho === 'admins'
            ? 'Who can create a group: only an administrator. '
            : groupsWho === 'everyone'
              ? 'Who can create a group: anybody in Jarvis, your contractors '
                + 'included. '
              : 'Who can create a group: any member of staff, but not a '
                + 'contractor. '}
          Members can add only people they could already message, so a group
          never reaches further than the messaging switches (Chat, calls &amp;
          meetings → Messaging) allow.
        </p>

        {/*
          * CONTRACTORS AND GROUPS (migration 079). Gordon, 23 October:
          * "contractors can create groups". A switch, on by default once 079
          * has run, that matters while the choice above is Staff - Everyone
          * already includes contractors and Administrators only excludes
          * them, so it is shown ticked-and-grey or unticked-and-grey there.
          * And which membership rules a contractor may use: never wider than
          * who they may message (the switches above), which the server checks
          * on top - these can only narrow it.
          */}
        {rules.chat_group_rules_ready === true ? (
          <>
            <label className="ar-check" id="s-groups-contractors">
              <input
                type="checkbox"
                checked={groupsWho === 'everyone' || (groupsWho === 'staff' && contractorGroups)}
                disabled={!canEdit || groupsWho !== 'staff' || saving === 'chat_groups_contractors'}
                onChange={() => setRule('chat_groups_contractors', 'chat-groups-contractors', !contractorGroups)}
              />
              <span className="ar-label">Contractors can create groups</span>
            </label>
            <p className="ar-consequence">
              {groupsWho === 'everyone'
                ? 'Included in Everyone above.'
                : groupsWho === 'admins'
                  ? 'Not while only administrators may create groups.'
                  : contractorGroups
                    ? 'On: a contractor can start a group with the people they can already message.'
                    : 'Off: only staff start groups; contractors can still be added to them.'}
            </p>
            <fieldset className="ar-choice">
              <legend className="ar-label">Rules a contractor may use for a group’s members</legend>
              {[
                ['contractors', 'All contractors', 'needs Contractors can message each other'],
                ['staff', 'All staff, or a department', 'needs Everyone can message everyone'],
                ['everyone', 'Everyone', 'needs Everyone can message everyone']
              ].map(([value, label, note]) => (
                <label className="ar-check" key={value}>
                  <input
                    type="checkbox"
                    value={value}
                    checked={contractorRules.includes(value)}
                    disabled={!canEdit || saving === 'chat_group_rules_contractors'}
                    onChange={() => setRule('chat_group_rules_contractors', 'chat-group-rules-contractors',
                      contractorRules.includes(value)
                        ? contractorRules.filter((k) => k !== value)
                        : [...contractorRules, value])}
                  />
                  <span className="ar-label">{label}</span>
                  <span className="ar-quiet"> — {note}</span>
                </label>
              ))}
            </fieldset>
            <p className="ar-consequence">
              A group by rule keeps itself up to date: people join it and leave it as
              they join and leave the firm. Nobody, contractor or staff, can make one
              that covers people they could not message anyway.
            </p>
          </>
        ) : (
          <p className="ar-consequence">
            Contractors creating groups, and groups by rule, need migration 079 — run migrate:079.
          </p>
        )}
      </div>
      )}

      {/*
        * PROFILE PHOTOS (migration 080).
        *
        * Gordon: "Allow users to upload a picture of themselves so that the
        * round circles in the chat can be exchanged into a face pic." Three
        * positions, a ladder like the one above, and each says the thing an
        * administrator most needs to know before choosing it: hiding is not
        * deleting. Below it, every photo there is, so one that should not be
        * there can be taken down - but never one chosen for somebody.
        */}
      {show('photos') && (
      <div className="ar-rule" data-rule="photos" id="s-photos">
        <fieldset className="ar-choice ar-choice-first">
          <legend className="ar-label">Profile photos</legend>
          {[
            ['allowed', 'Everybody can add one'],
            ['staff', 'Staff only'],
            ['off', 'Off — initials everywhere']
          ].map(([value, label]) => (
            <label className="ar-check" key={value}>
              <input
                type="radio"
                name="profile-photos"
                value={value}
                checked={photosMode === value}
                disabled={!canEdit || saving === 'profile_photos'}
                onChange={() => setRule('profile_photos', 'profile-photos', value)}
              />
              <span className="ar-label">{label}</span>
            </label>
          ))}
        </fieldset>
        <p className="ar-consequence">
          {photosMode === 'off'
            ? 'Off: every circle shows initials. Nobody can add a photo, but anybody '
              + 'can still remove theirs. Nothing is deleted — turning photos back on '
              + 'brings every one back.'
            : photosMode === 'staff'
              ? "Staff only: staff photos show; contractors' photos are hidden and they "
                + 'cannot add one. Hidden, not deleted.'
              : 'Everybody: staff and contractors can each add a photo of themselves. '
                + 'A photo is seen by the people who can already see that person in chat.'}
        </p>
        <p className="ar-label ar-sub" id="s-photo-moderation">Photos people have added</p>
        <PhotoModeration canEdit={canEdit} />
      </div>
      )}

      {/*
        * CALLS WITHOUT A TICKET.
        *
        * Gordon, 15 October: a setting "where i can allow calls to be made
        * without a ticket being chosen (General)". Until it is on, the Call
        * button on the General thread of the chat screen is disabled with a
        * note saying a call is recorded on a ticket - which is the rule the
        * whole session feature was built on, because "who looked at whose
        * machine for how long" is an audit answer and the ticket is where it
        * belongs.
        *
        * Both positions are spelled out because the consequence of ON is
        * not "people can call more": it is that some calls are recorded in
        * a conversation rather than on a ticket, and an administrator
        * deciding this needs to see that sentence before ticking the box.
        */}
      {show('calls') && (
      <div className="ar-rule" id="s-calls-without-ticket">
        <label className="ar-check">
          <input
            type="checkbox"
            checked={freeCalls}
            disabled={!canEdit || saving === 'calls_without_ticket'}
            onChange={() => setRule('calls_without_ticket', 'calls-without-ticket', !freeCalls)}
          />
          <span className="ar-label">Calls may be made without a ticket</span>
        </label>
        <p className="ar-consequence">
          {freeCalls
            ? 'On: two people can ring each other from their General conversation '
              + 'on the chat screen, with no ticket chosen. The call — who was in '
              + 'it, how long a screen was shared, what was typed — is recorded in '
              + 'that conversation instead of on a ticket, and only the two of '
              + 'them or an administrator can join it. Calls started from a ticket '
              + 'are unchanged.'
            : 'Off: every call is started from a ticket and recorded on it. The '
              + 'Call button on the General thread stays greyed out and says why. '
              + 'Turning this off later does not end a call already in progress.'}
        </p>
      </div>
      )}

      {/*
        * ADMINISTRATORS READING OTHER PEOPLE'S CONVERSATIONS (migration 050).
        *
        * Gordon: "The conversations and attachments in general should be
        * saved and admin can access them if necessary." They are always
        * saved; this decides whether an administrator may open them, in
        * Reports → Chat archive.
        *
        * It is workplace monitoring, the same kind of decision as the device
        * heartbeat, so the warning is not a footnote: under data-protection
        * law staff have to be told before it starts, and a box ticked first
        * and an email sent afterwards is the order that gets a firm into
        * trouble. The warning shows while it is ON as well, because the
        * person who finds it on is often not the person who turned it on.
        */}
      {show('review') && (
      <div className="ar-rule" id="s-admin-review">
        <label className="ar-check">
          <input
            type="checkbox"
            checked={adminReview}
            disabled={!canEdit || saving === 'chat_admin_review'}
            onChange={() => setRule('chat_admin_review', 'chat-admin-review', !adminReview)}
          />
          <span className="ar-label">Administrators may read other people’s conversations</span>
        </label>
        <p className="ar-consequence">
          {adminReview
            ? 'On: an administrator can open any General or group conversation, '
              + 'and the files sent in it, from Advanced → Chat archive. Each look '
              + 'needs a reason, and who looked, at what, why and when is recorded '
              + 'there for good. Ticket conversations are not part of this — they '
              + 'are on the ticket already.'
            : 'Off: conversations and their files are kept, but nobody can read a '
              + 'conversation they are not in. The record of earlier looks stays '
              + 'readable in Advanced → Chat archive.'}
        </p>
        <p className="ar-note">
          Reading staff’s messages is monitoring. Staff should be told — what may be
          read, why, and who can read it — before this is switched on.
        </p>
      </div>
      )}

      {/*
        * RECORDING CALLS (migration 065). Gordon, 22 October: calls
        * recordable at the user's option, with a transcript and, from the
        * transcript, a summary and to-dos.
        *
        * Three decisions, in one block because they are one subject. Whether
        * at all; whether people are merely told or must each agree first -
        * announce is the default, because a banner nobody can miss plus a
        * chime is what every meeting tool does, and ask is there for a firm
        * that wants every voice on a recording to have said yes to it; and
        * how long the recording itself is kept. The transcript outlives the
        * recording on purpose, and the sentence says so: it is the part
        * people come back for, and it is a fraction of the size.
        */}
      {show('recording') && (
      <div className="ar-rule" data-rule="recording" id="s-recording">
        {legacyOff ? (
          <p className="ar-note" role="note">
            Recording is still switched off by the old “Calls can be recorded” rule. Migration 088
            moves that into the switch above and removes the old rule — run npm run migrate:088.
            Until then nobody can record, whatever the switch says.
          </p>
        ) : !recording && (
          <p className="ar-consequence ar-consequence-flush">
            Nobody can record while Call recording &amp; transcription is Off above. These two
            choices apply as soon as it is on again.
          </p>
        )}
        <fieldset className="ar-choice ar-choice-first" id="s-consent" disabled={!recording || undefined}>
          <legend className="ar-label">Before recording</legend>
          {[
            ['announce', 'Announce to everyone'],
            ['ask', 'Ask everyone to accept']
          ].map(([value, label]) => (
            <label className="ar-check" key={value}>
              <input
                type="radio"
                name="recording-consent"
                value={value}
                checked={consent === value}
                disabled={!canEdit || !recording || saving === 'recording_consent'}
                onChange={() => setRule('recording_consent', 'recording-consent', value)}
              />
              <span className="ar-label">{label}</span>
            </label>
          ))}
        </fieldset>
        <p className="ar-consequence">
          {consent === 'ask'
            ? 'Ask: nothing is recorded until every other person on the call has '
              + 'pressed Accept, and one Decline cancels it for everybody. The '
              + 'banner still shows once it starts.'
            : 'Announce: the moment somebody presses Record, everybody on the call '
              + 'sees a red Recording banner with the clock running and hears a '
              + 'chime. Nobody is asked first.'}
        </p>

        <div className="ar-numbers">
          <label className="ar-number" data-rule="recording-language" id="s-language">
            <span>Transcribe recordings in</span>
            <select
              value={recLanguage}
              disabled={!canEdit || !recording || saving === 'recording_language'}
              onChange={(e) => setRule('recording_language', 'recording-language', e.target.value)}
            >
              <option value="mixed">Malti + English</option>
              <option value="auto">Detect - any of the five</option>
              <option value="en-GB">English only</option>
              <option value="mt-MT">Malti only</option>
              <option value="it-IT">Italiano only</option>
              <option value="de-DE">Deutsch only</option>
              <option value="fr-FR">Français only</option>
            </select>
          </label>
        </div>
        <p className="ar-consequence">
          {recLanguage === 'mixed'
            ? 'Malti + English: the default for every recording, and the person pressing Record can '
              + 'still choose another. One transcription at the usual cost - Azure decides phrase by '
              + 'phrase which of the two is spoken; a switch in the middle of one sentence is written '
              + 'in that phrase’s main language.'
            : recLanguage === 'auto'
              ? 'Detect: Azure picks among English, Malti, Italian, German and French, phrase by phrase. '
                + 'Five candidates are less accurate than two; choose Malti + English if that is what '
                + 'the firm speaks.'
              : 'One language: every recording is transcribed in it unless the person pressing Record '
                + 'chooses another. Anything said in another language comes out garbled.'}
        </p>

        <div className="ar-numbers">
          <label className="ar-number" id="s-retention">
            <span>Keep recordings for</span>
            <input
              type="number" min="0" step="1"
              disabled={!canEdit || saving === 'recording_retention_days'}
              defaultValue={retention}
              key={`retention-${retention}`}
              onBlur={(e) => Number(e.target.value) !== retention
                && setNumber('recording_retention_days', 'recording-retention-days', e.target.value)}
            />
            <span>days</span>
          </label>
        </div>
        <p className="ar-consequence">
          {retention > 0
            ? `A recording is deleted ${retention} days after the call unless an `
              + 'administrator has chosen to keep it. The transcript, the summaries '
              + 'and the to-dos stay.'
            : 'Zero keeps every recording for ever. The transcript, the summaries '
              + 'and the to-dos stay in any case.'}
        </p>
      </div>
      )}

      {error && <p className="ar-error">{error}</p>}
      {!canEdit && (
        <p className="ar-quiet">
          These are read-only for you — an administrator can change them.
        </p>
      )}
    </div>
  );
};

export default AppRules;
